import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  NgbDropdownModule,
  NgbModal,
  NgbPaginationModule,
} from '@ng-bootstrap/ng-bootstrap';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBan,
  faCalendarDays,
  faRotateRight,
  faSortDown,
  faSortUp,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';

import {
  ScheduleRescheduleModalComponent,
  ScheduleRescheduleModalResult,
} from '../schedule-reschedule-modal/schedule-reschedule-modal.component';

import { ConfirmationModalComponent } from '@main/confirmation-modal/confirmation-modal.component';

import {
  parseScheduleDate,
  toScheduleTimestamp,
} from '@main/utils/date-time.util';

type SortDirection = 'asc' | 'desc';

export type ScheduleStatus =
  | 'Scheduled'
  | 'Done'
  | 'Failed'
  | 'Cancelled';

export type ScheduleItemCategory =
  | 'Proposal'
  | 'Invoice'
  | 'Email'
  | 'Payment';

export type ScheduleRow = {
  scheduleId: string;
  source: string;
  projectName: string;
  itemCategory: ScheduleItemCategory | string;
  itemId: string;
  status: ScheduleStatus | string;
  scheduledFor: string;
};

@Component({
  selector: 'app-schedule-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbPaginationModule,
    NgbDropdownModule,
    FontAwesomeModule,
  ],
  templateUrl: './schedule-table.component.html',
  styleUrls: ['./schedule-table.component.css'],
})
export class ScheduleTableComponent {
  private readonly dataSignal = signal<ScheduleRow[]>([]);

  /**
   * Rows returned by the API for the current page.
   */
  @Input()
  set data(value: ScheduleRow[] | null | undefined) {
    this.dataSignal.set(value ?? []);
  }

  /**
   * Total number of records returned by the API.
   *
   * Example:
   * {
   *   count: 150,
   *   items: [...]
   * }
   */
  @Input() totalCount = 0;

  /**
   * Current UI page.
   * Starts at 1 because ngb-pagination is 1-based.
   */
  @Input() page = 1;

  /**
   * Current API page size / limit.
   */
  @Input() limit = 10;

  /**
   * Current search value coming from parent.
   */
  @Input() searchValue = '';

  /**
   * API pagination/search events.
   */
  @Output() pageChange = new EventEmitter<number>();
  @Output() limitChange = new EventEmitter<number>();
  @Output() searchChange = new EventEmitter<string>();

  /**
   * Existing schedule actions.
   */
  @Output() retry = new EventEmitter<ScheduleRow>();
  @Output() viewJobSchedule = new EventEmitter<ScheduleRow>();
  @Output() cancel = new EventEmitter<ScheduleRow>();

  @Output() reschedule =
    new EventEmitter<ScheduleRescheduleModalResult>();

  faRotateRight = faRotateRight;
  faBan = faBan;
  faCalendarDays = faCalendarDays;
  faSortUp = faSortUp;
  faSortDown = faSortDown;

  readonly rows = computed<ScheduleRow[]>(() =>
    this.dataSignal(),
  );

  readonly scheduledForSort =
    signal<SortDirection>('asc');

  /**
   * Sorting remains client-side.
   *
   * Because pagination is API based, this only sorts the
   * rows returned for the current API page.
   */
  readonly sortedRows = computed<ScheduleRow[]>(() => {
    const rows = [...this.rows()];

    return rows.sort((a, b) => {
      const aTime = this.toTimestamp(a.scheduledFor);
      const bTime = this.toTimestamp(b.scheduledFor);

      return this.scheduledForSort() === 'asc'
        ? aTime - bTime
        : bTime - aTime;
    });
  });

  rowForCancellation: ScheduleRow | null = null;

  constructor(
    private readonly modalService: NgbModal,
  ) {}

  /**
   * Search
   */
  search(value: string): void {
    const searchValue = (value ?? '').trim();

    this.searchChange.emit(searchValue);
  }

  clearSearch(input?: HTMLInputElement): void {
    if (input) {
      input.value = '';
    }

    this.searchChange.emit('');
  }

  /**
   * Pagination
   */
  setCurrentPage(page: number): void {
    this.pageChange.emit(page);
  }

  /**
   * Limit
   */
  setLimit(limit: number): void {
    this.limitChange.emit(Number(limit));
  }

  /**
   * Sorting
   */
  toggleScheduledForSort(): void {
    this.scheduledForSort.update((direction) =>
      direction === 'asc' ? 'desc' : 'asc',
    );
  }

  getScheduledForSortIcon(): IconDefinition {
    return this.scheduledForSort() === 'asc'
      ? this.faSortUp
      : this.faSortDown;
  }

  /**
   * Retry schedule
   */
  onRetry(row: ScheduleRow): void {
    if (!this.canRetry(row)) {
      return;
    }

    this.retry.emit(row);
  }

  /**
   * Cancel schedule
   */
  openDeleteContactConfirmationModal(
    row: ScheduleRow,
  ): void {
    this.rowForCancellation = row;

    const confirmationModalRef =
      this.modalService.open(
        ConfirmationModalComponent,
      );

    confirmationModalRef.componentInstance.message =
      'Are you sure you would like to cancel this schedule?';

    confirmationModalRef.componentInstance.actionBtnTitle =
      'Confirm';

    confirmationModalRef.componentInstance.confirmed.subscribe(
      () => {
        if (!this.rowForCancellation) {
          return;
        }

        this.onCancel(this.rowForCancellation);
      },
    );
  }

  onCancel(row: ScheduleRow): void {
    if (!this.canCancel(row)) {
      return;
    }

    this.cancel.emit(row);

    this.rowForCancellation = null;
  }

  /**
   * View job
   */
  viewJobModal(row: ScheduleRow): void {
    this.viewJobSchedule.emit(row);
  }

  /**
   * Reschedule
   */
  openRescheduleModal(row: ScheduleRow): void {
    if (!this.canReschedule(row)) {
      return;
    }

    const modalRef = this.modalService.open(
      ScheduleRescheduleModalComponent,
      {
        size: 'md',
        centered: true,
        backdrop: 'static',
        windowClass: 'schedule-reschedule-modal',
      },
    );

    modalRef.componentInstance.row = row;

    modalRef.result
      .then(
        (
          result: ScheduleRescheduleModalResult,
        ) => {
          if (!result) {
            return;
          }

          this.reschedule.emit(result);
        },
      )
      .catch(() => {
        // Modal dismissed
      });
  }

  /**
   * Action availability
   */
  canRetry(row: ScheduleRow): boolean {
    return (
      this.normalizeStatus(row.status) === 'failed'
    );
  }

  canCancel(row: ScheduleRow): boolean {
    return (
      this.normalizeStatus(row.status) ===
      'scheduled'
    );
  }

  canReschedule(row: ScheduleRow): boolean {
    return (
      this.normalizeStatus(row.status) ===
      'scheduled'
    );
  }

  /**
   * Status UI
   */
  getStatusClass(status: string): string {
    switch (this.normalizeStatus(status)) {
      case 'scheduled':
        return 'status-pill status-pill--scheduled';

      case 'done':
        return 'status-pill status-pill--done';

      case 'failed':
        return 'status-pill status-pill--failed';

      case 'cancelled':
        return 'status-pill status-pill--cancelled';

      default:
        return 'status-pill';
    }
  }

  /**
   * Date formatting
   */
  formatDateTime(value: string): string {
    if (!value) {
      return '-';
    }

    const date = parseScheduleDate(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  }

  trackByScheduleRow(
    _: number,
    row: ScheduleRow,
  ): string {
    return row.scheduleId;
  }

  private toTimestamp(value: string): number {
    return toScheduleTimestamp(value);
  }

  private normalizeStatus(status: string): string {
    return (status ?? '')
      .trim()
      .toLowerCase();
  }
}